<template>
  <div class="job-supervisor-assignment">
    <div class="supervisor-assignment-search">
      <b-form-input
        v-model="search"
        placeholder="Search supervisors"
        debounce="200"
        size="sm"
      />
    </div>

    <div v-if="isLoading" class="supervisor-assignment-loading">
      <b-spinner small variant="primary" />
    </div>

    <template v-else>
      <div
        v-if="visibleSupervisors.length === 0"
        class="supervisor-assignment-empty"
      >
        No supervisors available for this project.
      </div>

      <template v-else>
        <div class="supervisor-assignment-toolbar">
          <small> {{ selectedIds.length }} selected </small>
          <div class="supervisor-assignment-actions">
            <b-button
              variant="link"
              size="sm"
              class="supervisor-assignment-link"
              @click="selectAll"
            >
              Select all
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="supervisor-assignment-link"
              @click="clearAll"
            >
              Clear
            </b-button>
          </div>
        </div>

        <div class="supervisor-list">
          <div
            v-if="filteredSupervisors.length === 0"
            class="supervisor-assignment-empty"
          >
            No supervisors match "{{ search }}".
          </div>
          <b-form-checkbox
            v-for="supervisor in filteredSupervisors"
            :key="supervisor.id"
            :checked="selectedIds.includes(supervisor.id)"
            class="supervisor-option"
            @change="toggle(supervisor.id, $event)"
          >
            <span
              class="supervisor-option-name"
              :class="{
                'supervisor-option-name--fallback': !supervisor.full_name,
              }"
              >{{ displayName(supervisor) }}</span
            >
            <span v-if="supervisor.trade" class="supervisor-option-trade">
              {{ formatTrade(supervisor.trade) }}
            </span>
          </b-form-checkbox>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { formatTrade } from '@/utils/timesheetHelpers'

export default {
  name: 'JobSupervisorAssignment',

  props: {
    // v-model — array of selected supervisor ids
    value: {
      type: Array,
      default: () => [],
    },
    projectId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      supervisors: [],
      search: '',
      isLoading: false,
    }
  },

  computed: {
    selectedIds() {
      return Array.isArray(this.value) ? this.value : []
    },

    // Supervisors invited by email who haven't set a name yet come back with a
    // blank full_name and would render as an empty row. Hide them from the
    // picker — but keep any that are somehow already assigned to this job so
    // they stay visible and removable rather than silently stuck.
    visibleSupervisors() {
      return this.supervisors.filter(
        (s) =>
          (s.full_name && s.full_name.trim()) ||
          this.selectedIds.includes(s.id),
      )
    },

    filteredSupervisors() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.visibleSupervisors
      return this.visibleSupervisors.filter((s) =>
        this.displayName(s).toLowerCase().includes(term),
      )
    },
  },

  watch: {
    projectId() {
      this.loadSupervisors({ prune: true })
    },
  },

  created() {
    this.loadSupervisors()
  },

  methods: {
    formatTrade,

    // Fall back to the email address when a supervisor has no name yet, so a
    // row never renders blank if one slips past visibleSupervisors.
    displayName(supervisor) {
      return (
        (supervisor.full_name && supervisor.full_name.trim()) ||
        supervisor.email ||
        'Unnamed supervisor'
      )
    },

    async loadSupervisors({ prune = false } = {}) {
      this.isLoading = true
      try {
        this.supervisors = await this.$store.dispatch(
          'schedule/getAvailableSupervisors',
          { project_id: this.projectId || undefined },
        )
        if (prune) {
          // Drop selections no longer available after a project change.
          const available = new Set(this.supervisors.map((s) => s.id))
          const kept = this.selectedIds.filter((id) => available.has(id))
          if (kept.length !== this.selectedIds.length) {
            this.$emit('input', kept)
          }
        }
      } catch (err) {
        this.supervisors = []
      } finally {
        this.isLoading = false
      }
    },

    toggle(supervisorId, checked) {
      const next = this.selectedIds.filter((id) => id !== supervisorId)
      if (checked) next.push(supervisorId)
      this.$emit('input', next)
    },

    selectAll() {
      this.$emit(
        'input',
        this.visibleSupervisors.map((s) => s.id),
      )
    },

    clearAll() {
      this.$emit('input', [])
    },
  },
}
</script>

<style scoped>
.job-supervisor-assignment {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.supervisor-assignment-search /deep/ .form-control {
  height: 38px;
  border-color: #d1d5db;
  border-radius: 8px;
}

.supervisor-assignment-loading,
.supervisor-assignment-empty {
  padding: 1rem;
  color: #9ca3af;
  font-size: 0.82rem;
  text-align: center;
}

.supervisor-assignment-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  color: #6b7280;
  font-size: 0.78rem;
}

.supervisor-assignment-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.supervisor-assignment-link {
  padding: 0;
  font-size: 0.78rem;
  font-weight: 700;
}

.supervisor-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 0.45rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.supervisor-option {
  display: block;
  margin: 0;
  min-height: 52px;
  padding: 0.55rem 0.65rem 0.55rem 2.35rem;
  border-radius: 8px;
  color: #111827;
}

.supervisor-option + .supervisor-option {
  margin-top: 0.15rem;
}

.supervisor-option:hover {
  background: #ffffff;
}

.supervisor-option /deep/ .custom-control-label {
  display: block;
  line-height: 1.25;
}

.supervisor-option /deep/ .custom-control-label::before,
.supervisor-option /deep/ .custom-control-label::after {
  top: 0.1rem;
  left: -1.8rem;
}

.supervisor-option-name {
  font-size: 0.82rem;
  font-weight: 700;
}

.supervisor-option-name--fallback {
  font-weight: 600;
  color: #6b7280;
}

.supervisor-option-trade {
  display: block;
  margin-top: 0.1rem;
  color: #6b7280;
  font-size: 0.72rem;
}
</style>
